import { FC, useMemo } from "react";
import { Nullable } from "service";
import GenericTable, {
  RenderGenericRowFunction,
  SortOptions,
} from "../../../pages/dashboard/components/generic-table/generic-table";
import InteractableCell from "../../../pages/dashboard/components/generic-table/interactable-cell";
import { Box, Chip, Typography, useTheme } from "@mui/material";
import { capitalize } from "lodash";
import { formatDateTime } from "../../../utils/string-helpers";
import { useNavigate } from "react-router-dom";
import { generateRoute } from "ui";
import { AdRoutes } from "../../../constants/ad-routes";
import { EventNotificationsResDto } from "service/api/estabrookSchemas";
import { NotificationsTypeEnum } from "service/types/enum/notifications-enum";

const sanitizedHeader = [
  {
    label: "Title",
    value: "title",
    size: "15%",
  },
  {
    label: "Message",
    value: "message",
    size: "35%",
  },
  {
    label: "Type",
    value: "type",
    size: "10%",
  },
  {
    label: "Delivery",
    value: "delivery",
    size: "15%",
  },
  {
    label: "Date Modified",
    value: "dateModified",
    size: "15%",
    sortable: true,
    sortableValue: "updatedAt",
  },
  {
    label: "",
    value: "actions",
    size: "10%",
  },
];

export interface RouteNotificationsTableProps {
  data?: EventNotificationsResDto[];
  sortOptions?: Nullable<SortOptions>;
  sortOptionsChange?: (value: Nullable<SortOptions>) => void;
  isLoading?: boolean;
  refetch?: () => void;
}

const RouteNotificationsTable: FC<RouteNotificationsTableProps> = (props) => {
  const { data = [], sortOptions, sortOptionsChange, isLoading } = props;
  const { palette } = useTheme();
  const navigate = useNavigate();

  const onEditPress = (eventNotificationData: EventNotificationsResDto) => {
    navigate(
      generateRoute(AdRoutes.editEventNotification, {
        notificationId: eventNotificationData.id,
      }),
      { state: { eventNotificationData } },
    );
  };

  const renderRowLabelMap: Record<
    string,
    RenderGenericRowFunction<EventNotificationsResDto>
  > = useMemo(
    () => ({
      title: ({ headerValue, data: { notificationTitle } }) => ({
        label: notificationTitle,
        value: headerValue,
      }),
      message: ({
        headerValue,
        data: { notificationCustomMessage, notificationDefaultMessage },
      }) => ({
        label: (
          <Typography
            variant="body2"
            sx={{
              maxWidth: 450,
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {notificationCustomMessage || notificationDefaultMessage}
          </Typography>
        ),
        value: headerValue,
      }),
      type: ({ headerValue, data: { notificationType } }) => ({
        label: (
          <Box sx={{ display: "flex", gap: "6px" }}>
            {notificationType && notificationType.length > 0 ? (
              (notificationType as NotificationsTypeEnum[]).map(
                (type: NotificationsTypeEnum, index: number) => (
                  <Chip
                    key={index}
                    label={capitalize(type)}
                    sx={{
                      backgroundColor:
                        type === NotificationsTypeEnum.push
                          ? palette.grey[400]
                          : palette.grey[500],
                    }}
                    data-testid="notification-type"
                  />
                ),
              )
            ) : (
              <Chip
                label="Not Set"
                sx={{
                  backgroundColor: palette.grey[300],
                }}
                data-testid="notification-type"
              />
            )}
          </Box>
        ),
        value: headerValue,
      }),
      delivery: ({ headerValue, data: { description } }) => ({
        label: <Typography variant="body2">{description}</Typography>,
        value: headerValue,
      }),
      dateModified: ({ headerValue, data: { updatedAt } }) => ({
        label: formatDateTime(updatedAt),
        value: headerValue,
      }),
      actions: ({ headerValue, data }) => {
        return {
          label: (
            <InteractableCell label={<></>} onEdit={() => onEditPress(data)} />
          ),
          value: headerValue,
        };
      },
    }),
    [palette.grey],
  );

  return (
    <GenericTable
      sx={{ mt: "20px" }}
      headers={sanitizedHeader}
      data={data}
      renderRowLabel={renderRowLabelMap}
      sortOptions={sortOptions}
      onSortChange={sortOptionsChange}
      isLoading={isLoading}
    />
  );
};

export default RouteNotificationsTable;
